import type { Ref } from 'vue'
import { reactive, watch } from 'vue'
import useCanvas from '@canvas-builder/composables/useCanvas.ts'
import type { FileRetrieve } from '@openapi'
import type { ContentListSection } from '@canvas-builder/types/canvas'

const files = reactive({
  loading: {} as Record<string, true>,
  loaded: {} as Record<string, FileRetrieve>,
})

const isFileProcessed = (id: string) => id in files.loading || id in files.loaded

const getMissingItems = (newItems: ContentListSection[]) => {
  const missingFiles = new Set<string>()

  newItems.forEach((item) => {
    const { type, file_id } = item

    if (type === 'file' && file_id && !isFileProcessed(file_id)) {
      missingFiles.add(file_id)
    }
  })

  return {
    missingFiles: Array.from(missingFiles),
  }
}

const DEFAULT_FILE_FIELDS = [
  'id',
  'global_id',
  'name',
  'type',
  'size',
  'status',
  'version',
  'content_type',
  'content_extension',
  'content_length',
  'content_url',
  'content_thumbnails',
  'original_extension',
  'original_url',
  'thumbnail_url',
  'download_type',
  'zip_size',
  'zip_url',
  'access_type',
  'role',
  'permissions',
  'pspdfkit_server_url',
  'pspdfkit_document_id',
  'pspdfkit_auth_payload',
  'metadata',
  'app_metadata',
  'tags',
  'published_at',
  'expires_at',
  'instance_id',
  'folder_id',
  'folder',
  'shared_link',
  'owned_by',
  'created_at',
  'created_by',
  'modified_at',
  'modified_by',
]

export default () => {
  const { fetchFile, listFiles, isSetListFilesApi } = useCanvas()

  async function fetchFiles(ids: string[]) {
    if (!fetchFile.value) {
      return null
    }

    try {
      return (await Promise.all(ids.map((id) => fetchFile.value!(id)))) as FileRetrieve[]
    } catch (e) {
      console.error('[App.vue]: failed to fetch files', e)
      return null
    }
  }

  const watchMissingFiles = (rawItems: Ref<ContentListSection[]>) =>
    watch(
      rawItems,
      async (newItems) => {
        const { missingFiles } = getMissingItems(newItems)

        if (missingFiles.length === 0) return

        missingFiles.forEach((id) => (files.loading[id] = true))

        const fileResponse = isSetListFilesApi.value
          ? ((await listFiles.value?.({
              filters: { id__in: missingFiles.join(',') },
              fields: [...DEFAULT_FILE_FIELDS, 'content_thumbnails'].join(','),
            })) as FileRetrieve[])
          : await fetchFiles(missingFiles)

        fileResponse?.forEach((file) => {
          if (file) {
            files.loaded[file.id] = file
          }
        })

        missingFiles.forEach((id) => delete files.loading[id])
      },
      { immediate: true },
    )

  return {
    files,
    watchMissingFiles,
  }
}
